import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Breadcrumb component for navigation (English code/comments)
 */
const Breadcrumb = () => {
  const location = useLocation();

  // Route to Chinese name mapping for breadcrumbs
  const routeMap = {
    '/': '总览',
    '/customers': '客户管理',
    '/appointments': '预约管理',
    '/treatment-records': '调理记录',
    '/billing': '结算管理',
    '/follow-up-tasks': '随访任务',
    '/reports/income-settlement': '收入与结算',
    '/reports/service-analysis': '服务效果分析',
    '/reports/follow-up-compliance': '随访依从率',
    '/treatments': '调理项目管理',
    '/practitioners': '调理师管理',
    '/venues': '调理中心管理',
    '/users': '员工管理',
    '/research-projects': '科研项目管理',
    '/datasets': '数据集管理',
    '/profile': '个人资料',
    '/settings': '设置'
  };

  // Generate breadcrumb items
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const breadcrumbs = [{ name: '总览', path: '/', current: pathSegments.length === 0 }];

    if (pathSegments.length === 0) {
      return breadcrumbs;
    }

    let currentPath = '';

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;

      // Main route
      if (routeMap[currentPath]) {
        breadcrumbs.push({
          name: routeMap[currentPath],
          path: currentPath,
          current: isLast
        });
      }
      // Detail pages (ID param)
      else if (index === 1 && pathSegments[0] && routeMap[`/${pathSegments[0]}`]) {
        let detailName = 'Detail';
        switch (pathSegments[0]) {
          case 'customers':
            detailName = '客户详情及历史记录'; break;
          case 'appointments':
            detailName = '预约详情'; break;
          case 'treatment-records':
            detailName = '调理记录详情'; break;
          case 'treatments':
            detailName = '调理项目详情'; break;
          case 'practitioners':
            detailName = '调理师详情'; break;
          case 'venues':
            detailName = '场地/房间详情'; break;
          default:
            detailName = '详情';
        }
        breadcrumbs.push({
          name: detailName,
          path: currentPath,
          current: isLast
        });
      }
      // Special routes (e.g., new)
      else if (segment === 'new' && pathSegments[0] && routeMap[`/${pathSegments[0]}`]) {
        let newName = 'New';
        switch (pathSegments[0]) {
          case 'customers':
            newName = 'New Customer'; break;
          case 'appointments':
            newName = 'New Appointment'; break;
          case 'treatment-records':
            newName = 'New Treatment Record'; break;
          case 'treatments':
            newName = 'New Treatment Item'; break;
          case 'practitioners':
            newName = 'New Practitioner'; break;
          case 'venues':
            newName = 'New Venue/Room'; break;
          default:
            newName = 'New';
        }
        breadcrumbs.push({
          name: newName,
          path: currentPath,
          current: isLast
        });
      }
    });
    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // 在首页时不显示面包屑
  if (breadcrumbs.length <= 1) {
    return (
      <div className="flex items-center">
        <h1 className="text-lg font-semibold text-gray-900">总览</h1>
      </div>
    );
  }

  return (
    <nav className="flex" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {breadcrumbs.map((breadcrumb, index) => (
          <li key={breadcrumb.path} className="flex items-center">
            {index > 0 && (
              <svg
                className="flex-shrink-0 h-4 w-4 text-gray-400 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            {breadcrumb.current ? (
              <span className="text-sm font-semibold text-gray-900">
                {breadcrumb.name}
              </span>
            ) : (
              <Link
                to={breadcrumb.path}
                className="text-sm font-medium text-gray-500 hover:text-emerald-600 transition-colors duration-200"
              >
                {breadcrumb.name}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

/**
 * Main layout component for authenticated pages
 * @param {Object} props - Component props
 * @returns {React.ReactElement} Layout component
 */
const MainLayout = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Navigation groups and links (Chinese display names)
  const navGroups = [
    {
      section: '总览',
      items: [
        {
          name: '总览', path: '/', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          )
        }
      ]
    },
    {
      section: '客户与预约',
      items: [
        {
          name: '客户管理', path: '/customers', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          )
        },
        {
          name: '预约管理', path: '/appointments', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )
        },
        {
          name: '结算管理', disabled: true,path: '/billing', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 018 0v2M9 17H7a2 2 0 01-2-2v-2a2 2 0 012-2h2m0 0V7a4 4 0 118 0v4m-8 0h8" />
            </svg>
          )
        }
      ]
    },
    {
      section: '调理服务',
      items: [
        {
          name: '调理记录', path: '/treatment-records', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          )
        },
        
        {
          name: '随访管理', disabled: true, path: '/follow-up-tasks', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )
        }
      ]
    },
    {
      section: '运营报表',
      items: [
        {
          name: '收入与结算', disabled: true, path: '/reports/income-settlement', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3zm0 0V4m0 7v7m-7-7h14" />
            </svg>
          )
        },
        // {
        //   name: '服务效果分析',
        //   disabled: true,
        //   icon: (
        //     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17a4 4 0 01-4-4V5a4 4 0 018 0v8a4 4 0 01-4 4zm0 0v4m0-4h4m-4 0H7" />
        //     </svg>
        //   )
        // },
        {
          name: '随访依从率', disabled: true, path: '/reports/follow-up-compliance', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 018 0v2M9 17H7a2 2 0 01-2-2v-2a2 2 0 012-2h2m0 0V7a4 4 0 118 0v4m-8 0h8" />
            </svg>
          )
        }
      ]
    },
    {
      section: '资源配置',
      items: [
        {
          name: '调理中心管理', path: '/venues', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          )
        },
        {
          name: '调理项目管理', path: '/treatments', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          )
        },
        // {
        //   name: '员工管理', disabled: true, path: '/users', icon: (
        //     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m9-4a4 4 0 11-8 0 4 4 0 018 0z" />
        //     </svg>
        //   )
        // },
        {
          name: '调理师管理', path: '/practitioners', icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        }
        
      ]
    },
    {
      section: '协同科研管理',
      items: [
        // {
        //   name: '科研项目',
        //   disabled: true,
        //   icon: (
        //     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        //     </svg>
        //   )
        // },
        {
          name: '数据资产管理', 
          path: '/datasets', 
          disabled: true,
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a4 4 0 004 4h10a4 4 0 004-4V7" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 3v4a4 4 0 01-4 4 4 4 0 01-4-4V3" />
            </svg>
          )
        }
      ]
    }
  ];

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check if a navigation link is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 flex flex-col z-50 md:relative md:flex md:translate-x-0 transform transition duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } w-72 h-full bg-white border-r border-gray-200 p-3`}
      >
        {/* Logo Section */}
        <div className="flex items-center px-3 py-4 border-b border-gray-100">
          <div className="flex items-center text-gray-900">
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <span className="ml-3 text-lg font-bold whitespace-nowrap">国保中医调理中心</span>
            <span className="ml-2 px-2 py-0.5 border border-emerald-500 text-emerald-600 text-xs rounded max-w-[48px] text-center leading-tight break-words">仅供演示</span>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 py-4 overflow-y-auto">
          {navGroups.map((group, groupIdx) => (
            <div key={group.section} className="mb-4">
              <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {group.section}
              </div>
              <nav className="space-y-1">
                {group.items.map((link, index) => (
                  link.disabled ? (
                    <div
                      key={link.name}
                      className="group flex items-center px-3 py-2.5 text-base font-medium rounded-lg mx-2 text-gray-400 cursor-not-allowed bg-gray-50"
                      title="敬请期待"
                    >
                      <div className="mr-3 h-5 w-5 text-gray-300">{link.icon}</div>
                      {link.name}
                    </div>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`${isActive(link.path)
                        ? 'bg-emerald-50 text-emerald-700 border-r-2 border-emerald-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        } group flex items-center px-3 py-2.5 text-base font-medium transition-colors duration-200 rounded-lg mx-2`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <div className={`mr-3 h-5 w-5 ${isActive(link.path) ? 'text-emerald-500' : 'text-gray-400 group-hover:text-gray-500'
                        }`}>{link.icon}</div>
                      {link.name}
                    </Link>
                  )
                ))}
              </nav>
            </div>
          ))}
        </div>

        {/* User Profile */}
        <div className="border-t border-gray-100 pt-4 pb-3 relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex items-center px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium">
                {currentUser?.name ? currentUser.name.charAt(0) : '用'}
              </div>
            </div>
            <div className="ml-3 flex-1 text-left">
              <p className="text-sm font-medium text-gray-700">{currentUser?.name || '用户'}</p>
              <p className="text-xs text-gray-500">
                {currentUser?.role === 'admin' ? '管理员' :
                  currentUser?.role === 'practitioner' ? '调理师' :
                    currentUser?.role === 'receptionist' ? '前台' :
                      currentUser?.role || '员工'}
              </p>
            </div>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* User dropdown menu */}
          {userMenuOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-2 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 py-1">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{currentUser?.name || '用户'}</p>
                <p className="text-xs text-gray-500">
                  {currentUser?.role === 'admin' ? '管理员' :
                    currentUser?.role === 'practitioner' ? '调理师' :
                      currentUser?.role === 'receptionist' ? '前台' :
                        currentUser?.role || '员工'}
                </p>
              </div>
              <Link
                to="/profile"
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setUserMenuOpen(false)}
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                个人资料
              </Link>
              <Link
                to="/settings"
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setUserMenuOpen(false)}
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                设置
              </Link>
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  handleLogout();
                }}
                className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                登出
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top navigation */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white/90 backdrop-blur-lg shadow-lg border-b border-white/20">
          <button
            onClick={() => setSidebarOpen(true)}
            className="px-4 md:hidden"
          >
            <svg className="h-6 w-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Breadcrumb navigation */}
          <div className="flex-1 flex items-center px-4">
            <Breadcrumb />
          </div>

          {/* Notification bell */}
          <div className="flex items-center pr-4">
            <button className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none bg-gray-50">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;